import { useState, useEffect, useCallback } from 'react';
import { CostFilterDto, CostSummaryDto, OrphanRow } from '../Model/CostType';
import { costService } from '../Services/CostService';

interface UseCostSummaryResult {
  summary: CostSummaryDto | null;
  orphans: OrphanRow[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

function extractOrphans(summary: CostSummaryDto): OrphanRow[] {
  return (summary.Breakdown ?? [])
    .filter((r) => r.ReviewRequired?.toLowerCase() === 'yes')
    .map((r) => ({
      Name: r.ResourceName || r.ResourceId || 'unknown',
      Cost: r.Cost,
      ResourceGroup: r.ResourceGroup || '',
      ReviewReason: r.ReviewReason ?? '',
      Type: r.ResourceType || r.ServiceType || r.Scope || '',
      ResourceId: r.ResourceId,
    }));
}

export function useCostSummary(filter: CostFilterDto): UseCostSummaryResult {
  const [summary, setSummary] = useState<CostSummaryDto | null>(null);
  const [orphans, setOrphans] = useState<OrphanRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState<number>(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await costService.GetCostSummary(filter);
        if (!cancelled) {
          setSummary(data);
          setOrphans(extractOrphans(data));
        }
      } catch (err) {
        if (!cancelled)
          setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [filter.From, filter.To, tick]);

  return { summary, orphans, loading, error, refetch };
}
